import React from 'react';
import { Link } from 'react-router-dom';
import { Award, ArrowRight, CheckCircle, Sparkles, ShieldCheck, Droplet, CheckCircle2, Leaf, Car, Sun, Footprints, Layers, Waves, Home as HomeIcon } from 'lucide-react';
import { benefits, applications, contactInfo } from '../data';
import SEO from '../components/SEO';

// Map icon names to Lucide components
const iconMap = {
  Sparkles: Sparkles,
  ShieldCheck: ShieldCheck,
  Droplet: Droplet,
  CheckCircle: CheckCircle,
  Leaf: Leaf,
  Award: Award,
  Car: Car,
  Sun: Sun,
  Footprints: Footprints,
  Layers: Layers,
  Waves: Waves,
  Home: HomeIcon
};

// Map application titles to their high-quality images
const appImages = {
  'Indkørsler': 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg',
  'Terrasser': 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/badd6eea-b45e-42e2-bf84-1f92e8d91be3.jpg',
  'Gangstier': 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/f0f16b94-8f6e-4a76-b4de-669e74e9d9c5.jpg',
  'Trapper': 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/aaacfe6e-3946-4690-a3d7-a4f1cfdfe45e.jpg',
  'Poolområder & vådzoner': 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/d9e66450-9d19-4e2f-89b7-8625a6886679.jpg',
  'Garager & overdækkede arealer': 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/7ce14f8a-f404-421a-a756-58097c3cf9a1.jpg'
};

export default function Home() {
  return (
    <div className="pt-16 md:pt-24 xl:pt-28">
      <SEO
        title="Helstøbt Stenbelægning | Eksklusiv Belægning Uden Ukrudt | Stenbelægning.dk"
        description="Specialist i helstøbt stenbelægning og resinbunden stenmel til indkørsler, terrasser, trapper og havestier. Fugefri, slidstærk og vedligeholdelsesfri overflade. Få et gratis og uforpligtende tilbud i dag!"
      />
      {/* Hero Section */}
      <section className="relative bg-stone-100 overflow-hidden pt-20 pb-20 md:pt-40 md:pb-24 xl:py-32">
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg"
            alt="Eksklusiv villaindkørsel med helstøbt stenbelægning"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl bg-white/95 backdrop-blur-md p-8 sm:p-12 rounded-3xl shadow-2xl border border-stone-200/60">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-stone-900 leading-tight">
              Helstøbt stenbelægning med eksklusivt udtryk og lang levetid
            </h1>
            
            <p className="mt-6 text-base sm:text-lg text-stone-700 leading-relaxed">
              Vi udfører helstøbt stenbelægning til indkørsler, terrasser, gangarealer og trapper. Belægningen består af naturlige småsten, der bindes sammen med en stærk resin og udlægges som én sammenhængende overflade.
            </p>
            
            <p className="mt-4 text-sm sm:text-base text-stone-500">
              Resultatet er en flot, stærk og vedligeholdelsesvenlig belægning uden løse sten, synlige samlinger eller ukrudt mellem fuger.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl text-base font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
              >
                Få et tilbud
              </Link>
              <Link
                to="/referencer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl text-base font-bold bg-transparent text-stone-900 hover:bg-stone-900 hover:text-white border border-stone-900 hover:-translate-y-1 transition-all duration-300"
              >
                Se referencer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro / Highlight Section */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-stone-900 font-bold text-sm uppercase tracking-wider">Fremtidens belægning</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mt-2">
                Helstøbt stenbelægning til private og erhverv
              </h2>
              <p className="text-stone-600 mt-6 leading-relaxed">
                Helstøbt stenbelægning fra Vuba is en moderne løsning til dig, der ønsker en slidstærk og eksklusiv overflade. Belægningen is velegnet til både nye anlæg og renovering af eksisterende belægninger.
              </p>
              <p className="text-stone-600 mt-4 leading-relaxed">
                Uanset om det gælder en ny indkørsel, en terrasse eller en trapperenovering, sikrer vores professionelle resin-bundne system en fugefri overflade, der dræner vandet væk og holder sig flot år efter år.
              </p>
              <div className="mt-8">
                <Link
                  to="/helstoebt-stenbelaegning"
                  className="inline-flex items-center gap-2 text-stone-900 hover:text-stone-700 font-bold transition-colors underline"
                >
                  Læs mere om belægningen <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="relative overflow-hidden shadow-2xl aspect-video lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6055090a-18d2-4c2c-9d28-28de4d83c95a.jpg"
                alt="Professionel fagmand der udlægger helstøbt stenbelægning"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Subpages / Belægninger Section */}
      <section className="py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-emerald-400 font-bold text-sm uppercase tracking-wider">Vores specialer</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2 uppercase tracking-wider">
              Belægninger & Materialer
            </h2>
            <p className="text-stone-300 mt-4 text-base sm:text-lg">
              Udforsk vores tre hovedområder inden for helstøbt stenbelægning, lodret belægning og Vuba materialer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* 1. Helstøbt Stenbelægning */}
            <Link
              to="/helstoebt-stenbelaegning"
              className="group relative h-96 overflow-hidden rounded-2xl border border-stone-800 hover:border-emerald-500/50 shadow-xl transition-all duration-300 block flex flex-col justify-end"
            >
              <img 
                src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg" 
                alt="Helstøbt Stenbelægning" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/95 via-stone-950/50 to-stone-950/10" />
              <div className="relative p-6 sm:p-8 z-10">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600/90 text-white mb-3 backdrop-blur-sm">
                  Vores primære belægning
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-wider uppercase group-hover:text-emerald-400 transition-colors duration-300 mb-2">
                  Helstøbt Stenbelægning
                </h3>
                <p className="text-stone-300 text-sm leading-relaxed mb-4 line-clamp-2">
                  Sømløs, drænende og slidstærk stenbelægning til indkørsler, terrasser, stier og gangarealer.
                </p>
                <div className="inline-flex items-center gap-2 text-white font-bold text-sm group-hover:text-emerald-400 transition-colors">
                  <span>Læs mere</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* 2. Lodret Stenbelægning */}
            <Link
              to="/lodret-stenbelaegning"
              className="group relative h-96 overflow-hidden rounded-2xl border border-stone-800 hover:border-emerald-500/50 shadow-xl transition-all duration-300 block flex flex-col justify-end"
            >
              <img 
                src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/aaacfe6e-3946-4690-a3d7-a4f1cfdfe45e.jpg" 
                alt="Lodret Stenbelægning" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/95 via-stone-950/50 to-stone-950/10" />
              <div className="relative p-6 sm:p-8 z-10">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-stone-800/90 text-stone-200 mb-3 backdrop-blur-sm">
                  Til trapper & mure
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-wider uppercase group-hover:text-emerald-400 transition-colors duration-300 mb-2">
                  Lodret Stenbelægning
                </h3>
                <p className="text-stone-300 text-sm leading-relaxed mb-4 line-clamp-2">
                  Specialiseret udlægning til trapper, trappetrin, vertikale flader og mure for et sammenhængende udtryk.
                </p>
                <div className="inline-flex items-center gap-2 text-white font-bold text-sm group-hover:text-emerald-400 transition-colors">
                  <span>Læs mere</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* 3. Vuba Materialer */}
            <Link
              to="/vuba-materialer"
              className="group relative h-96 overflow-hidden rounded-2xl border border-stone-800 hover:border-emerald-500/50 shadow-xl transition-all duration-300 block flex flex-col justify-end"
            >
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6220a5-03b6d8988038445f889647a00d1e7e3f-mv2.jpg" 
                alt="Vuba Materialer" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/95 via-stone-950/50 to-stone-950/10" />
              <div className="relative p-6 sm:p-8 z-10">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-stone-800/90 text-stone-200 mb-3 backdrop-blur-sm">
                  Kvalitet & resin
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-wider uppercase group-hover:text-emerald-400 transition-colors duration-300 mb-2">
                  Vuba Materialer
                </h3>
                <p className="text-stone-300 text-sm leading-relaxed mb-4 line-clamp-2">
                  Certificerede Vuba Europe produkter, 100% UV-resistent binder og over 38 smukke naturstensfarver.
                </p>
                <div className="inline-flex items-center gap-2 text-white font-bold text-sm group-hover:text-emerald-400 transition-colors">
                  <span>Læs mere</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-stone-900 font-bold text-sm uppercase tracking-wider">Hvorfor vælge os?</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mt-2">
              Fordele ved helstøbt stenbelægning
            </h2>
            <p className="text-stone-900 mt-4">
              Vores belægningssystem kombinerer æstetik med uovertruffen holdbarhed og funktionalitet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon] || CheckCircle;
              return (
                <div 
                  key={index} 
                  className={`p-8 transition-all duration-300 border-stone-200
                    border-b last:border-b-0
                    md:border-b md:[&:nth-child(2n-1)]:border-r md:[&:nth-child(2n)]:border-r-0 md:[&:nth-last-child(-n+2)]:border-b-0
                    lg:border-b lg:[&:nth-child(3n-2)]:border-r lg:[&:nth-child(3n-1)]:border-r lg:[&:nth-child(3n)]:border-r-0 lg:[&:nth-last-child(-n+3)]:border-b-0
                  `}
                >
                  <div className="h-12 w-12 rounded-xl text-stone-900 flex items-center justify-start mb-5">
                    <IconComponent className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">{benefit.title}</h3>
                  <p className="text-stone-900 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service & Om Os CTA Section */}
      <section className="pt-0 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Service Block */}
            <div className="bg-emerald-600 text-[#f1f1f1] rounded-3xl p-8 sm:p-12 border border-emerald-500 shadow-xl flex flex-col justify-between">
              <div>
                <span className="text-emerald-100 font-bold text-sm uppercase tracking-wider">Serviceaftale</span>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2 mb-4">
                  Vedligeholdelsesaftale til din belægning
                </h3>
                <p className="text-emerald-50 leading-relaxed mb-6">
                  Selvom helstøbt stenbelægning kræver minimal vedligeholdelse, kan en serviceaftale forlænge levetiden og sikre, at overfladen bliver ved med at fremstå flot.
                </p>
              </div>
              <div>
                <Link
                  to="/service"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold bg-white text-emerald-900 hover:bg-emerald-50 hover:-translate-y-1 transition-all duration-300"
                >
                  Se serviceaftale <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Om Os Block */}
            <div className="bg-white rounded-3xl p-8 sm:p-12 border border-stone-200/60 shadow-xl flex flex-col justify-between">
              <div>
                <span className="text-stone-900 font-bold text-sm uppercase tracking-wider">Om os</span>
                <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 mt-2 mb-4">
                  Uddannet fagmand med speciale i belægning
                </h3>
                <p className="text-stone-900 leading-relaxed mb-6">
                  Virksomheden er specialiseret i helstøbt stenbelægning og arbejder udelukkende med denne type belægning. Det betyder, at du får en fagmand med fokus, erfaring og uddannelse inden for området.
                </p>
              </div>
              <div>
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold bg-stone-900 text-white hover:bg-stone-800 hover:-translate-y-1 transition-all duration-300"
                >
                  Læs mere om os <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/edfc4191-0236-4b5a-915a-2a04051a9ea3.jpg"
            alt="Stenbelægning baggrund"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-950/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Skal vi hjælpe dig med dit næste projekt?
          </h2>
          <p className="mt-4 text-lg max-w-2xl mx-auto text-[#f1f1f1]">
            Kontakt os i dag for en uforpligtende snak og et skræddersyet tilbud på din nye, helstøbte stenbelægning.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-bold bg-white text-stone-950 hover:bg-stone-100 hover:-translate-y-1 transition-all duration-300"
            >
              Ring {contactInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
